import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  LifeBuoy, MessageCircle, Inbox, MessageSquare, BookOpen, FileText, Activity,
  Headphones, CalendarClock, Users, Mail, Phone, ArrowRight, CheckCircle2, Clock,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SeoHead from '../components/SeoHead';

const API = `${(process.env.CRA_API_BASE_URL || process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_BACKEND_URL)}/api`;

function useHashScroll() {
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) {
      window.scrollTo({ top: 0, behavior: 'instant' });
      return;
    }
    const id = hash.replace('#', '');
    const el = document.getElementById(id);
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - 140;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  }, [hash]);
}

function Section({ id, eyebrow, title, children, dark = false }) {
  return (
    <section
      id={id}
      data-testid={`support-section-${id}`}
      className={`scroll-mt-[140px] py-16 sm:py-20 ${dark ? 'text-white' : 'bg-white text-gray-900'}`}
      style={
        dark
          ? { background: 'linear-gradient(135deg, #c9a227 0%, #b8911e 45%, #8a6710 100%)' }
          : undefined
      }
    >
      <div className="zh-container">
        {eyebrow && (
          <div
            className={`text-[11px] font-bold tracking-[0.22em] uppercase mb-3 ${dark ? 'text-white/90' : 'text-zinc-700'}`}
          >
            {eyebrow}
          </div>
        )}
        {title && (
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight mb-8 max-w-3xl">
            {title}
          </h2>
        )}
        {children}
      </div>
    </section>
  );
}

/* Tiny ticket form — POSTs to /api/contact (existing endpoint),
   so every submission shows up in the Admin contacts list. */
function TicketForm() {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [status, setStatus] = useState({ state: 'idle', msg: '' });
  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;
    setStatus({ state: 'sending', msg: '' });
    try {
      const r = await fetch(`${API}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          subject: form.subject || 'Support ticket',
          lang: 'en',
        }),
      });
      if (!r.ok) throw new Error('failed');
      setStatus({ state: 'ok', msg: 'Ticket received. We will reply within one business day.' });
      setForm({ name: '', email: '', subject: '', message: '' });
    } catch {
      setStatus({ state: 'err', msg: 'Could not send. Please email support@zhelta.com directly.' });
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid="support-ticket-form"
      className="rounded-2xl border border-gray-200 bg-white p-6 sm:p-8 max-w-3xl space-y-4 shadow-sm"
    >
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-[12px] font-semibold uppercase tracking-wider text-gray-500 mb-1">Your name</label>
          <input
            value={form.name}
            onChange={update('name')}
            data-testid="support-ticket-name"
            required
            className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:border-[#c9a227] text-sm"
          />
        </div>
        <div>
          <label className="block text-[12px] font-semibold uppercase tracking-wider text-gray-500 mb-1">Email</label>
          <input
            type="email"
            value={form.email}
            onChange={update('email')}
            data-testid="support-ticket-email"
            required
            className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:border-[#c9a227] text-sm"
          />
        </div>
      </div>
      <div>
        <label className="block text-[12px] font-semibold uppercase tracking-wider text-gray-500 mb-1">Subject</label>
        <input
          value={form.subject}
          onChange={update('subject')}
          data-testid="support-ticket-subject"
          placeholder="Briefly, what is going on?"
          className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:border-[#c9a227] text-sm"
        />
      </div>
      <div>
        <label className="block text-[12px] font-semibold uppercase tracking-wider text-gray-500 mb-1">Describe the issue</label>
        <textarea
          value={form.message}
          onChange={update('message')}
          data-testid="support-ticket-message"
          required
          rows={5}
          className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:border-[#c9a227] text-sm resize-y"
        />
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="submit"
          disabled={status.state === 'sending'}
          data-testid="support-ticket-submit"
          className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full text-white text-[13px] font-semibold disabled:opacity-60"
          style={{ background: 'linear-gradient(90deg, #ffb347 0%, #f5a623 50%, #ff8c1a 100%)' }}
        >
          {status.state === 'sending' ? 'Sending…' : 'Submit ticket'}
          <ArrowRight className="w-3.5 h-3.5" />
        </button>
        {status.msg && (
          <span
            data-testid="support-ticket-status"
            className={`text-sm ${status.state === 'ok' ? 'text-green-700' : status.state === 'err' ? 'text-red-700' : 'text-gray-500'}`}
          >
            {status.msg}
          </span>
        )}
      </div>
    </form>
  );
}

const KB_CATEGORIES = [
  { title: 'Getting Started', body: 'Account setup, choosing a plan, and onboarding flows for individuals and SMBs.', icon: BookOpen },
  { title: 'IT Systems', body: 'Common how-tos for managed IT, cybersecurity, networking, and remote support.', icon: LifeBuoy },
  { title: 'Marketplace orders', body: 'Tracking, returns, invoicing, and bulk procurement through ZHELTA Services.', icon: Inbox },
  { title: 'Academy enrolment', body: 'Programs, schedules, payments, mentor access, and certifications.', icon: FileText },
];

const DOC_LINKS = [
  { title: 'ZHELTA Systems Solutions Catalog', href: '/systems/solutions', desc: 'Full catalogue of IT services with descriptions, pricing, and request flows.' },
  { title: 'Pricing Reference', href: '/systems/pricing', desc: 'Pricing tiers across every Systems solution, in your local currency.' },
  { title: 'Get Started Wizard', href: '/systems/get-started', desc: 'A two-minute questionnaire that routes you to the matching plan.' },
  { title: 'Academy Programs', href: '/academy#programs', desc: 'Active learning programs and how to enrol.' },
];

const PARTNER_TYPES = [
  { title: 'Reseller', body: 'Resell ZHELTA managed-IT subscriptions to your own client base with revenue share.' },
  { title: 'Vendor', body: 'List your hardware, software, or services on the ZHELTA Marketplace.' },
  { title: 'Education partner', body: 'Co-deliver Academy programs at schools, bootcamps, or training centres.' },
];

export default function Support() {
  useHashScroll();

  return (
    <div className="min-h-screen bg-white">
      <SeoHead
        pageKey="support"
        path="/support"
        title="Support — ZHELTA"
        description="Real human support across ZHELTA Systems, Services, and Academy. Submit a ticket, browse the knowledge base, check system status, or talk to sales — all from one place."
      />
      <Header />

      <main>
        {/* Hero */}
        <section
          className="relative text-white overflow-hidden"
          style={{ background: 'linear-gradient(135deg, #c9a227 0%, #b8911e 45%, #8a6710 100%)' }}
        >
          <div
            className="absolute inset-0 opacity-30"
            style={{
              background:
                'radial-gradient(circle at 20% 20%, rgba(255,255,255,0.35) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(0,0,0,0.18) 0%, transparent 60%)',
            }}
          />
          <div className="relative zh-container py-20 sm:py-24">
            <div className="text-[11px] font-bold tracking-[0.22em] uppercase mb-4 text-white/90">Support</div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight max-w-4xl mb-5">
              Real human support, end to end.
            </h1>
            <p className="text-lg sm:text-xl text-white/85 max-w-3xl mb-8 leading-relaxed">
              Whether you have an urgent IT issue, a marketplace question, or an Academy enrolment query — you get the
              same direct line to real people. No chatbots, no runaround.
            </p>
            <div className="flex flex-wrap gap-3">
              <a
                href="#contact-support"
                data-testid="support-hero-contact"
                className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-white text-zinc-800 text-[13px] font-semibold hover:brightness-95 transition-all"
              >
                <MessageCircle className="w-4 h-4" /> Contact support
              </a>
              <a
                href="#submit-a-ticket"
                data-testid="support-hero-ticket"
                className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full border-2 border-white/70 text-white text-[13px] font-semibold hover:bg-white hover:text-zinc-800 transition-colors"
              >
                <Inbox className="w-4 h-4" /> Submit a ticket
              </a>
            </div>
          </div>
        </section>

        {/* Get Help · Contact Support */}
        <Section id="contact-support" eyebrow="Get Help" title="Contact support.">
          <p className="text-base text-gray-600 max-w-2xl mb-8">
            Reach us by your preferred channel. We monitor every inbox during business hours and rotate on-call cover for urgent issues.
          </p>
          <div className="grid sm:grid-cols-3 gap-5">
            <a
              href="mailto:support@zhelta.com"
              data-testid="support-channel-email"
              className="group rounded-2xl border border-gray-200 bg-white p-6 hover:border-[#c9a227]/60 transition-colors block"
            >
              <Mail className="w-6 h-6 text-zinc-700 mb-3" />
              <div className="text-base font-bold text-gray-900 mb-1">Email</div>
              <div className="text-sm text-gray-600 mb-3">Best for non-urgent issues. Replies within one business day.</div>
              <span className="text-[13px] font-semibold text-zinc-700">support@zhelta.com</span>
            </a>
            <a
              href="#live-chat"
              data-testid="support-channel-chat"
              className="group rounded-2xl border border-gray-200 bg-white p-6 hover:border-[#c9a227]/60 transition-colors block"
            >
              <MessageSquare className="w-6 h-6 text-zinc-700 mb-3" />
              <div className="text-base font-bold text-gray-900 mb-1">Live chat</div>
              <div className="text-sm text-gray-600 mb-3">Real people, working hours. Tap the chat icon at the bottom-right.</div>
              <span className="text-[13px] font-semibold text-zinc-700 inline-flex items-center gap-1">
                Open chat <ArrowRight className="w-3.5 h-3.5" />
              </span>
            </a>
            <a
              href="tel:+19142006175"
              data-testid="support-channel-phone"
              className="group rounded-2xl border border-gray-200 bg-white p-6 hover:border-[#c9a227]/60 transition-colors block"
            >
              <Phone className="w-6 h-6 text-zinc-700 mb-3" />
              <div className="text-base font-bold text-gray-900 mb-1">Phone</div>
              <div className="text-sm text-gray-600 mb-3">For urgent client issues. Available Mon&ndash;Fri, 9 am&ndash;6 pm local.</div>
              <span className="text-[13px] font-semibold text-zinc-700">Call 914-200-6175 &rarr;</span>
            </a>
          </div>
        </Section>

        {/* Submit a Ticket */}
        <Section id="submit-a-ticket" eyebrow="Get Help" title="Submit a ticket.">
          <p className="text-base text-gray-600 max-w-2xl mb-8">
            Open a written ticket so we have everything in one place. Include error messages, screenshots, and what you&rsquo;ve already tried.
          </p>
          <TicketForm />
        </Section>

        {/* Live Chat */}
        <Section id="live-chat" eyebrow="Get Help" title="Live chat with the team." dark>
          <p className="text-lg text-white/90 max-w-3xl mb-8 leading-relaxed">
            Tap the chat bubble at the bottom-right of any page to start a conversation. A real ZHELTA team member will pick up
            during working hours, and your message is queued outside hours so we can follow up first thing.
          </p>
          <div className="grid sm:grid-cols-3 gap-5">
            <div className="rounded-2xl border border-white/30 bg-white/15 backdrop-blur-sm p-5">
              <Clock className="w-6 h-6 text-white mb-2" />
              <div className="text-sm font-bold text-white mb-1">Working hours</div>
              <div className="text-sm text-white/85">Mon&ndash;Fri · 9 am&ndash;6 pm local</div>
            </div>
            <div className="rounded-2xl border border-white/30 bg-white/15 backdrop-blur-sm p-5">
              <MessageSquare className="w-6 h-6 text-white mb-2" />
              <div className="text-sm font-bold text-white mb-1">Real humans</div>
              <div className="text-sm text-white/85">No bots. The same engineers who fix your stack reply.</div>
            </div>
            <div className="rounded-2xl border border-white/30 bg-white/15 backdrop-blur-sm p-5">
              <Inbox className="w-6 h-6 text-white mb-2" />
              <div className="text-sm font-bold text-white mb-1">Off-hours</div>
              <div className="text-sm text-white/85">Messages queue and we reply first thing the next morning.</div>
            </div>
          </div>
        </Section>

        {/* Self-Service · Knowledge Base */}
        <Section id="knowledge-base" eyebrow="Self-Service" title="Knowledge base.">
          <p className="text-base text-gray-600 max-w-2xl mb-8">
            Browse common how-tos and answers across our three divisions. The articles you tell us are missing become next week&rsquo;s additions.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {KB_CATEGORIES.map((c) => (
              <div key={c.title} data-testid={`support-kb-${c.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`} className="rounded-2xl border border-gray-200 bg-white p-6 hover:border-[#c9a227]/60 transition-colors">
                <c.icon className="w-6 h-6 text-zinc-700 mb-3" />
                <h3 className="text-base font-bold text-gray-900 mb-2">{c.title}</h3>
                <p className="text-sm text-gray-600 leading-6 mb-3">{c.body}</p>
                <a href="mailto:support@zhelta.com?subject=KB%20Request%3A%20" className="text-[13px] font-semibold text-zinc-700 inline-flex items-center gap-1 hover:underline">
                  Request an article <ArrowRight className="w-3.5 h-3.5" />
                </a>
              </div>
            ))}
          </div>
        </Section>

        {/* Self-Service · Documentation */}
        <Section id="documentation" eyebrow="Self-Service" title="Documentation.">
          <p className="text-base text-gray-600 max-w-2xl mb-8">
            Public references for what we offer, how it&rsquo;s priced, and how to get started.
          </p>
          <div className="grid sm:grid-cols-2 gap-5">
            {DOC_LINKS.map((d) => (
              <a
                key={d.title}
                href={d.href}
                data-testid={`support-doc-${d.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                className="group rounded-2xl border border-gray-200 bg-white p-6 hover:border-[#c9a227]/60 transition-colors block"
              >
                <FileText className="w-5 h-5 text-zinc-700 mb-3" />
                <h3 className="text-base font-bold text-gray-900 mb-1.5">{d.title}</h3>
                <p className="text-sm text-gray-600 leading-6 mb-3">{d.desc}</p>
                <span className="text-[13px] font-semibold text-zinc-700 inline-flex items-center gap-1">
                  Open reference <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
                </span>
              </a>
            ))}
          </div>
        </Section>

        {/* Self-Service · System Status */}
        <Section id="system-status" eyebrow="Self-Service" title="System status.">
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6 sm:p-8 max-w-3xl">
            <div className="flex items-center gap-3 mb-4">
              <span className="inline-flex w-3 h-3 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-sm font-bold uppercase tracking-wider text-emerald-700">All systems operational</span>
            </div>
            <p className="text-sm text-gray-700 leading-6 mb-4">
              Public website, customer portal, marketplace, and admin dashboard are healthy. Updates posted here whenever
              we ship maintenance or detect a degradation. Want to be paged automatically?
            </p>
            <a
              href="mailto:info@zhelta.com?subject=Subscribe%20me%20to%20status%20updates"
              data-testid="support-status-subscribe"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white text-zinc-800 border border-zinc-400 text-[13px] font-semibold hover:bg-zinc-100 transition-colors"
            >
              <Activity className="w-4 h-4" /> Subscribe to status updates
            </a>
          </div>
        </Section>

        {/* Business · Talk to Sales */}
        <Section id="talk-to-sales" eyebrow="Business" title="Talk to sales.">
          <div className="grid lg:grid-cols-2 gap-8 items-start">
            <div className="space-y-4 text-[15px] text-gray-700 leading-7 max-w-xl">
              <p>
                Looking to bring ZHELTA into your business? Our sales team helps small and mid-sized teams pick the right
                plan, scope custom builds, and onboard cleanly.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-zinc-700 mt-1 shrink-0" /> Plan recommendations across all Systems solutions</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-zinc-700 mt-1 shrink-0" /> Custom scope &amp; quote within 2 business days</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-zinc-700 mt-1 shrink-0" /> Multi-currency invoicing &amp; PO support</li>
              </ul>
            </div>
            <div className="rounded-2xl border border-zinc-300 bg-gradient-to-br from-[#fff8e7] to-[#fef3c7] p-7">
              <Headphones className="w-6 h-6 text-zinc-700 mb-3" />
              <h3 className="text-xl font-extrabold mb-2">Reach our sales team</h3>
              <p className="text-sm text-gray-700 mb-5">Real reply within 1 business day. Faster if your case is urgent.</p>
              <div className="flex flex-wrap gap-3">
                <a
                  href="/systems/contact"
                  data-testid="support-sales-contact"
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-white text-[13px] font-semibold"
                  style={{ background: 'linear-gradient(90deg, #ffb347 0%, #f5a623 50%, #ff8c1a 100%)' }}
                >
                  Contact sales <ArrowRight className="w-3.5 h-3.5" />
                </a>
                <a
                  href="mailto:sales@zhelta.com"
                  data-testid="support-sales-email"
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[13px] font-semibold text-gray-900 border-2 border-gray-900 hover:bg-gray-900 hover:text-white transition-colors"
                >
                  <Mail className="w-3.5 h-3.5" /> sales@zhelta.com
                </a>
              </div>
            </div>
          </div>
        </Section>

        {/* Business · Schedule a Consultation */}
        <Section id="schedule-a-consultation" eyebrow="Business" title="Schedule a consultation.">
          <p className="text-base text-gray-600 max-w-2xl mb-8">
            Pick a time that works for you and one of our engineers will walk you through your stack, your constraints, and the
            best ZHELTA path forward. 30 minutes, no obligation.
          </p>
          <div className="rounded-2xl border border-gray-200 bg-white p-6 max-w-3xl">
            <div className="flex items-start gap-4 mb-5">
              <div className="w-12 h-12 rounded-xl bg-zinc-100 flex items-center justify-center shrink-0">
                <CalendarClock className="w-6 h-6 text-zinc-700" />
              </div>
              <div>
                <div className="text-base font-bold text-gray-900">Free 30-minute consultation</div>
                <div className="text-sm text-gray-500">Live video, multi-language available, recordings on request.</div>
              </div>
            </div>
            <a
              href="/systems/book-demo"
              data-testid="support-consultation-book"
              className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full text-white text-[13px] font-semibold"
              style={{ background: 'linear-gradient(90deg, #ffb347 0%, #f5a623 50%, #ff8c1a 100%)' }}
            >
              Book a slot <ArrowRight className="w-3.5 h-3.5" />
            </a>
          </div>
        </Section>

        {/* Business · Partner Program */}
        <Section id="partner-program" eyebrow="Business" title="Partner program." dark>
          <p className="text-lg text-white/90 max-w-3xl mb-10 leading-relaxed">
            Three ways to grow with ZHELTA. We work with partners who serve the same individuals and small businesses we do
            &mdash; and who care about doing the work properly.
          </p>
          <div className="grid sm:grid-cols-3 gap-5">
            {PARTNER_TYPES.map((p) => (
              <div
                key={p.title}
                data-testid={`support-partner-${p.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                className="rounded-2xl border border-white/30 bg-white/15 backdrop-blur-sm p-6"
              >
                <Users className="w-6 h-6 text-white mb-3" />
                <h3 className="text-base font-bold text-white mb-1.5">{p.title}</h3>
                <p className="text-sm text-white/85 leading-6">{p.body}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 flex flex-wrap items-center gap-3">
            <a
              href="mailto:sales@zhelta.com?subject=Partnership%20-%20Sales"
              data-testid="support-partner-apply"
              className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-white text-zinc-800 text-[13px] font-semibold hover:brightness-95 transition-all"
            >
              <Mail className="w-4 h-4" /> sales@zhelta.com
            </a>
            <a
              href="/systems/contact"
              className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full border-2 border-white/70 text-white text-[13px] font-semibold hover:bg-white hover:text-zinc-800 transition-colors"
            >
              Talk to us first
            </a>
          </div>
        </Section>
      </main>

      <Footer />
    </div>
  );
}
